/* Mobile-only enhancements for the existing SkyFire product-card component.
   Does not clone cards, replace forms, rewrite URLs, or send requests. */
(() => {
  'use strict';
  if (window.sfMobileProductCardsLoaded) return;
  window.sfMobileProductCardsLoaded = true;
  const media = window.matchMedia('(max-width: 699px)');
  const cardSelector = 'main product-card.sf-product-card';
  const sliders = new Map();
  const specLists = new Set();
  const prepared = new WeakSet();
  let queued = false;

  function fitSpecs(list) {
    if (!list.isConnected) return;
    if (!media.matches) {
      list.style.removeProperty('--sf-mobile-spec-columns');
      return;
    }
    const cells = [...list.querySelectorAll('.product-card__spec-value')];
    const width = list.clientWidth;
    if (!width || !cells.length) return;
    let needed = 0;
    cells.forEach(cell => {
      const span = cell.querySelector('.sf-mobile-spec-text') || cell;
      const range = document.createRange();
      range.selectNodeContents(span);
      needed = Math.max(needed, range.getBoundingClientRect().width + 12);
    });
    let columns = cells.length > 3 ? 2 : cells.length;
    while (columns > 1 && width / columns < needed) columns--;
    const value = String(columns);
    if (list.style.getPropertyValue('--sf-mobile-spec-columns') !== value) {
      list.style.setProperty('--sf-mobile-spec-columns', value);
    }
  }

  const resizeObserver = new ResizeObserver(entries => entries.forEach(e => fitSpecs(e.target)));

  function prepareSpecs(list) {
    if (!prepared.has(list)) {
      prepared.add(list);
      list.querySelectorAll('.product-card__spec-value').forEach(cell => {
        const original = cell.textContent;
        const value = original.trim();
        const label = /^[\d,.\s]+\s*mAh$/i.test(value) ? 'Battery'
          : /^[\d,.\s]+\s*(lm|lumens?)$/i.test(value) ? 'Lumens'
          : /^\d+\s*modes?$/i.test(value) ? 'Modes' : '';
        // Infer labels only from explicit units, never from metafield position.
        if (label) {
          const caption = document.createElement('span');
          caption.className = 'sf-mobile-spec-label';
          caption.textContent = label;
          const text = document.createElement('span');
          text.className = 'sf-mobile-spec-text';
          text.textContent = original;
          cell.replaceChildren(caption, text);
        }
      });
    }
    if (!specLists.has(list)) { specLists.add(list); resizeObserver.observe(list); }
    fitSpecs(list);
  }

  const fields = ['breakpoints', 'slidesPerView', 'slidesPerGroup', 'spaceBetween'];
  const pick = params => Object.fromEntries(fields.map(key => [key, params[key]]));

  function restore(slider, state) {
    const swiper = state.instance;
    if (!swiper.destroyed) {
      Object.assign(swiper.params, state.params);
      Object.assign(swiper.originalParams, state.original);
      swiper.currentBreakpoint = undefined;
      swiper.update();
    }
    sliders.delete(slider);
  }

  function syncSliders() {
    sliders.forEach((state, slider) => {
      if (!media.matches || !slider.isConnected || slider.swiper !== state.instance) restore(slider, state);
    });
    if (!media.matches) return;
    document.querySelectorAll('main yg-swiper').forEach(slider => {
      const wrapper = slider.querySelector(':scope > .swiper-wrapper');
      const slides = wrapper && [...wrapper.children].filter(el => el.classList.contains('swiper-slide'));
      // Exclude hero banners, scene grids, mixed image/video sliders and menus.
      if (!slides?.length || !slides.every(slide => slide.querySelector('product-card.sf-product-card'))) return;
      const swiper = slider.swiper;
      if (!swiper || swiper.destroyed || sliders.get(slider)?.instance === swiper) return;
      const state = {instance: swiper, params: pick(swiper.params), original: pick(swiper.originalParams)};
      sliders.set(slider, state);
      const breakpoints = {...swiper.params.breakpoints};
      breakpoints[0] = {...breakpoints[0], slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 12};
      Object.keys(breakpoints).forEach(key => {
        if (!key.startsWith('@') && Number(key) < 700) {
          breakpoints[key] = {...breakpoints[key], slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 12};
        }
      });
      [swiper.params, swiper.originalParams].forEach(params => {
        Object.assign(params, {breakpoints, slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 12});
      });
      swiper.currentBreakpoint = undefined;
      swiper.update();
    });
  }

  function sync() {
    queued = false;
    specLists.forEach(list => {
      if (!list.isConnected) { resizeObserver.unobserve(list); specLists.delete(list); }
    });
    document.querySelectorAll(`${cardSelector} .product-card__spec-list`).forEach(prepareSpecs);
    syncSliders();
  }
  function schedule() {
    if (!queued) { queued = true; requestAnimationFrame(sync); }
  }
  function start() {
    sync();
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
        node.nodeType === 1 && (node.matches('product-card, yg-swiper, .product-card__spec-list') ||
        node.querySelector('product-card, yg-swiper, .product-card__spec-list'))))) schedule();
    }).observe(document.body, {childList: true, subtree: true});
    customElements.whenDefined('yg-swiper').then(schedule);
    document.fonts?.ready.then(schedule);
    document.addEventListener('shopify:section:load', schedule);
    media.addEventListener('change', schedule);
    window.addEventListener('pageshow', schedule);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
